import React, { useMemo } from 'react';
import {
  AsyncCreatableSelect,
  AsyncSelect,
  ChakraStylesConfig,
  CreatableSelect,
  GroupBase,
  Select,
  chakraComponents,
} from 'chakra-react-select';
import { tokens } from '@everfi/design-system';
import { Box } from '@chakra-ui/react';

import { ComboboxProps, CustomComponentsConfig, DefaultReactSelectProps, OptionBase } from './types';
import { inlineMenuChakraStyles } from './styles';
import { Icon } from '../Icon';
import { i18n } from '../../../i18n/translate';
const { Control, ClearIndicator, LoadingIndicator } = chakraComponents;

// Defined outside the component so references are stable across renders.
// react-select/chakra-react-select closes the menu and resets state when
// `components` or `chakraStyles` change reference between renders.
const chakraReactSelectStyles: ChakraStylesConfig = {
  indicatorSeparator: () => ({
    display: 'none',
  }),
  groupHeading: provided => ({
    ...provided,
    fontSize: '2xs',
  }),
  option: provided => ({
    ...provided,
    fontSize: 'sm',
  }),
  clearIndicator: provided => ({
    ...provided,
    fontSize: 'sm',
    rounded: 'full',
  }),
  menu: provided => ({
    ...provided,
    svg: {
      color: 'icon-brand',
    },
  }),
};

const customComponents: CustomComponentsConfig<OptionBase, boolean, GroupBase<OptionBase>> = {
  Control: (props: React.ComponentProps<typeof Control>) => (
    <Control {...props}>
      {/* The Control already carries the input's start padding, so the gap belongs after the icon. */}
      <Box me="sm" alignSelf="center">
        <Icon prefix="far" iconName="magnifying-glass" />
      </Box>
      {props.children}
    </Control>
  ),
  ClearIndicator: (props: React.ComponentProps<typeof ClearIndicator>) => {
    const { innerProps } = props;
    const focusInput = () => {
      window.setTimeout(() => {
        const inputId = props.selectProps?.inputId ?? props.selectProps?.id;
        if (!inputId) return;

        document.getElementById(inputId)?.focus();
      });
    };
    const accessibleInnerProps = {
      ...innerProps,
      'aria-hidden': undefined,
      'aria-label': i18n.t('Clear selected options'),
      'data-action': 'clear-selected-options',
      role: 'button',
      tabIndex: 0,
      onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        event.stopPropagation();
        props.clearValue();
        focusInput();
      },
    };

    return (
      <ClearIndicator {...props} innerProps={accessibleInnerProps}>
        <Box>
          <Icon prefix="fas" iconName="xmark-circle" color={tokens.ColorsGrey500} />
        </Box>
      </ClearIndicator>
    );
  },
  LoadingIndicator: (props: React.ComponentProps<typeof LoadingIndicator>) => (
    <LoadingIndicator {...props}>
      <Icon prefix="fas" iconName="spinner" spin />
    </LoadingIndicator>
  ),
};

const stableStyles = {
  // Dialog's modal scroll-lock sets `pointer-events: none` on <body>, which a
  // document.body-portaled menu inherits unless it opts back in explicitly.
  menuPortal: (provided: object) => ({
    ...provided,
    // react-select's portal styles are plain CSS, so Chakra cannot resolve the token name here.
    zIndex: 'var(--udl-zIndices-popover, 1500)',
    pointerEvents: 'auto' as const,
  }),
};

export const Combobox = <Option extends OptionBase>({
  options,
  backspaceRemovesValue,
  dataAction,
  closeMenuOnSelect,
  isMulti = false,
  isAsync = false,
  isInvalid = false,
  id,
  inputId,
  components,
  chakraStyles,
  isCreatable = false,
  isClearable = false,
  isDisabled = false,
  isInline = false,
  isLoading = false,
  isSearchable,
  cacheOptions,
  defaultOptions,
  autoFocus,
  controlShouldRenderValue,
  menuIsOpen,
  inputValue,
  name,
  onChange,
  onInputChange,
  onBlur,
  formatOptionLabel,
  getOptionLabel,
  getOptionValue,
  value,
  loadOptions,
  variant,
  placeholder = 'Select...',
  menuPortalTarget = document.body,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  menuPosition,
}: ComboboxProps<Option>) => {
  // Identity must stay stable: react-select resets its menu state whenever
  // `chakraStyles` or `components` change reference between renders.
  const mergedChakraStyles = useMemo(
    () => ({
      ...chakraReactSelectStyles,
      ...(isInline ? inlineMenuChakraStyles : {}),
      ...chakraStyles,
    }),
    [isInline, chakraStyles]
  );

  const mergedComponents = useMemo(() => ({ ...customComponents, ...components }), [components]);

  const defaultReactSelectProps: DefaultReactSelectProps = {
    selectedOptionStyle: 'check',
    chakraStyles: mergedChakraStyles,
    tabSelectsValue: false,
    hideSelectedOptions: false,
    colorPalette: 'brand',
    tagVariant: 'subtle',
  };

  const commonProps: Partial<ComboboxProps<Option>> = {
    ...defaultReactSelectProps,
    options,
    backspaceRemovesValue,
    isMulti,
    isDisabled,
    isClearable,
    isLoading,
    isSearchable,
    // Keep menu open after selection in multi-select unless caller explicitly overrides
    closeMenuOnSelect: closeMenuOnSelect ?? !isMulti,
    id,
    inputId,
    autoFocus,
    controlShouldRenderValue,
    chakraStyles: mergedChakraStyles,
    // An inline list has no trigger to open it, so it stays open unless the caller says otherwise.
    menuIsOpen: menuIsOpen ?? (isInline || undefined),
    // React Select traps wheel events over a floating menu so the page behind stays put.
    // An inline list is part of that page, so the scroll has to chain through.
    captureMenuScroll: isInline ? false : undefined,
    inputValue,
    name,
    onChange,
    onInputChange,
    onBlur,
    formatOptionLabel,
    getOptionLabel,
    getOptionValue,
    value,
    placeholder,
    components: mergedComponents,
    menuPortalTarget: isInline ? null : menuPortalTarget,
    menuPosition,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    styles: stableStyles,
  };

  if (!isAsync) {
    const SelectComponent = isCreatable ? CreatableSelect : Select;
    return (
      <SelectComponent
        {...commonProps}
        invalid={isInvalid}
        // chakra-react-select types `variant` as Chakra's built-in Select union, but this wrapper takes the
        // design-system recipe variants as an open string, hence the cast.
        variant={variant as never}
        blurInputOnSelect={!isMulti}
        data-object-type="core-combobox"
        data-action={dataAction}
      />
    );
  }

  const AsyncSelectComponent = isCreatable ? AsyncCreatableSelect : AsyncSelect;
  return (
    <AsyncSelectComponent
      {...commonProps}
      invalid={isInvalid}
      variant={variant as never}
      blurInputOnSelect={!isMulti}
      loadOptions={loadOptions}
      cacheOptions={cacheOptions}
      defaultOptions={defaultOptions}
      data-object-type="core-combobox-async"
      data-action={dataAction}
    />
  );
};
